/*
 * Prev/next navigation inside the manage-load dialog.
 *
 * The awkward part is that the dialog steps through a *paginated* list: the
 * next load is usually the next row, but at a page boundary it lives in a page
 * that has not been fetched yet. So stepping is a two-part move — change the
 * page, then open the first or last row once the new page arrives — and the
 * intent has to survive the round trip.
 */

export type NavigationStep =
  | { kind: 'none' }
  | { kind: 'open'; index: number }
  | { kind: 'changePage'; page: number; then: PendingPageNavigation };

export type PendingPageNavigation = 'FIRST_ITEM' | 'LAST_ITEM' | null;

/** Where the open load sits in the current page, or -1 if it is not on it. */
export const findItemIndex = (rows: Array<{ id: string }>, currentItem: any): number => {
  if (!currentItem) {
    return -1;
  }

  return rows.findIndex((row) => row.id === currentItem.id);
};

// The `>= 0` guard matters: a load opened from the audit modal is often not on
// the current page, and `previousStep` refuses to move from there.
export const hasPreviousItem = (selectedItemIndex: number, page: number): boolean =>
  selectedItemIndex >= 0 && (selectedItemIndex > 0 || page > 1);

export const hasNextItem = (
  selectedItemIndex: number,
  rowCount: number,
  page: number,
  totalPages: number
): boolean => selectedItemIndex >= 0 && (selectedItemIndex < rowCount - 1 || page < totalPages);

/**
 * The "3 of 47" counter. One-based and across the whole result set, not the
 * current page, so it keeps counting up as pages turn.
 */
export const globalPosition = (
  selectedItemIndex: number,
  page: number,
  pageSize: number
): number | undefined =>
  selectedItemIndex >= 0 ? (page - 1) * pageSize + selectedItemIndex + 1 : undefined;

export const previousStep = (selectedItemIndex: number, page: number): NavigationStep => {
  if (selectedItemIndex < 0) {
    return { kind: 'none' };
  }

  if (selectedItemIndex > 0) {
    return { kind: 'open', index: selectedItemIndex - 1 };
  }

  // First row of the page: step back a page and open its last row.
  if (page > 1) {
    return { kind: 'changePage', page: Math.max(1, page - 1), then: 'LAST_ITEM' };
  }

  return { kind: 'none' };
};

export const nextStep = (
  selectedItemIndex: number,
  rowCount: number,
  page: number,
  totalPages: number
): NavigationStep => {
  if (selectedItemIndex < 0) {
    return { kind: 'none' };
  }

  if (selectedItemIndex < rowCount - 1) {
    return { kind: 'open', index: selectedItemIndex + 1 };
  }

  // Last row of the page: step forward a page and open its first row.
  if (page < totalPages) {
    return { kind: 'changePage', page: Math.min(totalPages, page + 1), then: 'FIRST_ITEM' };
  }

  return { kind: 'none' };
};

/**
 * Which row to open once a pending page change has landed, or null to keep
 * waiting.
 *
 * Waiting on `isFetching`/`isLoading` is load-bearing: acting on the previous
 * page's rows would re-open the load the user just stepped away from.
 */
export const resolvePendingNavigation = (
  pending: PendingPageNavigation,
  rows: any[],
  isBusy: boolean
): { action: 'wait' } | { action: 'clear' } | { action: 'open'; row: any } => {
  if (!pending) {
    return { action: 'wait' };
  }

  if (isBusy) {
    return { action: 'wait' };
  }

  // The new page came back empty — nothing to open, so drop the intent.
  if (rows.length === 0) {
    return { action: 'clear' };
  }

  return {
    action: 'open',
    row: pending === 'FIRST_ITEM' ? rows[0] : rows[rows.length - 1],
  };
};
